import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import ErrorBoundary, { RouteErrorFallback } from './components/ErrorBoundary'

// Page loader component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
)

// Lazy load pages for code splitting
// Public pages
// Swap these two lines to toggle between stealth and full landing page
// const LandingPage = lazy(() => import('./pages/StealthLandingPage'))  // Stealth mode (minimal)
const LandingPage = lazy(() => import('./pages/LandingPage'))  // Full marketing page
const LoginPage = lazy(() => import('./pages/LoginPage'))
const InterviewPage = lazy(() => import('./pages/InterviewPage'))
const SalesPage = lazy(() => import('./pages/SalesPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const SecurityPage = lazy(() => import('./pages/SecurityPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const CareersPage = lazy(() => import('./pages/CareersPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'))
const ConsultantsPage = lazy(() => import('./pages/ConsultantsPage'))
const PartnersPage = lazy(() => import('./pages/PartnersPage'))
const HRPage = lazy(() => import('./pages/HRPage'))
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'))
const IntegrationsMarketingPage = lazy(() => import('./pages/IntegrationsMarketingPage'))
const ROICalculatorPage = lazy(() => import('./pages/ROICalculatorPage'))
const CompareSurveysPage = lazy(() => import('./pages/CompareSurveysPage'))
const PilotCheckoutPage = lazy(() => import('./pages/PilotCheckoutPage'))
const PilotSetupWizard = lazy(() => import('./pages/PilotSetupWizard'))
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'))
const AcceptDebriefInvitePage = lazy(() => import('./pages/AcceptDebriefInvitePage'))
const DebriefMagicPage = lazy(() => import('./pages/DebriefMagicPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const ReportViewPage = lazy(() => import('./pages/ReportViewPage'))
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'))

// Solution pages
const SolutionInterviewsPage = lazy(() => import('./pages/SolutionInterviewsPage'))
const SolutionExitInterviewsPage = lazy(() => import('./pages/SolutionExitInterviewsPage'))
const SolutionOnboardingPage = lazy(() => import('./pages/SolutionOnboardingPage'))
const SolutionDebriefsPage = lazy(() => import('./pages/SolutionDebriefsPage'))

// Protected pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const CampaignPage = lazy(() => import('./pages/CampaignPage'))
const CampaignSetupPage = lazy(() => import('./pages/CampaignSetupPage'))
const CampaignComparePage = lazy(() => import('./pages/CampaignComparePage'))
const TeamPage = lazy(() => import('./pages/TeamPage'))
const DebriefPage = lazy(() => import('./pages/DebriefPage'))
const DebriefConfigPage = lazy(() => import('./pages/DebriefConfigPage'))
const DebriefDashboardPage = lazy(() => import('./pages/DebriefDashboardPage'))
const DebriefMembersPage = lazy(() => import('./pages/DebriefMembersPage'))
const DailyPulsePage = lazy(() => import('./pages/DailyPulsePage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const OnboardingPulsePage = lazy(() => import('./pages/OnboardingPulsePage'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))

// Partner pages
const PartnerDashboardPage = lazy(() => import('./pages/partner/PartnerDashboardPage'))
const PartnerOrganizationsPage = lazy(() => import('./pages/partner/PartnerOrganizationsPage'))
const PartnerRevenuePage = lazy(() => import('./pages/partner/PartnerRevenuePage'))

// Settings pages (consolidated)
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const ProfileSection = lazy(() => import('./pages/settings/profile/ProfileSection'))
const OrganizationSection = lazy(() => import('./pages/settings/organization/OrganizationSection'))
const IntegrationsSection = lazy(() => import('./pages/settings/integrations/IntegrationsSection'))
const BillingSection = lazy(() => import('./pages/settings/billing/BillingSection'))

// Legacy settings pages (for redirects)
const HRISSettingsPage = lazy(() => import('./pages/settings/HRISSettingsPage'))
const APIKeysPage = lazy(() => import('./pages/settings/APIKeysPage'))
const IntegrationsPage = lazy(() => import('./pages/settings/IntegrationsPage'))

// Layout components (not lazy loaded - used on every page)
import Layout from './components/Layout'
import PasswordGate from './components/PasswordGate'

// Wrapper component for lazy loaded pages with Suspense
const LazyPage = ({ children }) => (
  <ErrorBoundary fallback={<RouteErrorFallback />}>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
)

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  }

  return children
}

// Route guard for Campaigns feature access
function CampaignsRoute({ children }) {
  const { canAccessCampaigns, canAccessDebriefs, loading } = useAuth()

  if (loading) {
    return <PageLoader />
  }

  // If user doesn't have campaigns access, redirect to debriefs or settings
  if (!canAccessCampaigns) {
    if (canAccessDebriefs) {
      return <Navigate to="/app/debrief" replace />
    }
    return <Navigate to="/app/settings" replace />
  }

  return children
}

// Route guard for Debriefs feature access
function DebriefsRoute({ children }) {
  const { canAccessDebriefs, canAccessCampaigns, loading } = useAuth()

  if (loading) {
    return <PageLoader />
  }

  // If user doesn't have debriefs access, redirect to campaigns or settings
  if (!canAccessDebriefs) {
    if (canAccessCampaigns) {
      return <Navigate to="/app/campaigns" replace />
    }
    return <Navigate to="/app/settings" replace />
  }

  return children
}

function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LazyPage><LandingPage /></LazyPage>} />
        <Route path="/login" element={<LazyPage><LoginPage /></LazyPage>} />
        <Route path="/demo" element={<LazyPage><SalesPage /></LazyPage>} />
        <Route path="/interview/:token" element={<LazyPage><InterviewPage /></LazyPage>} />
        <Route path="/invite/:token" element={<LazyPage><AcceptInvitePage /></LazyPage>} />
        <Route path="/debrief/m/:token" element={<LazyPage><DebriefMagicPage /></LazyPage>} />
        <Route path="/debrief-invite/:token" element={<LazyPage><AcceptDebriefInvitePage /></LazyPage>} />
        <Route path="/solutions/interviews" element={<LazyPage><SolutionInterviewsPage /></LazyPage>} />
        <Route path="/solutions/exit-interviews" element={<LazyPage><SolutionExitInterviewsPage /></LazyPage>} />
        <Route path="/solutions/onboarding" element={<LazyPage><SolutionOnboardingPage /></LazyPage>} />
        <Route path="/solutions/debriefs" element={<LazyPage><SolutionDebriefsPage /></LazyPage>} />
        <Route path="/pricing" element={<LazyPage><PricingPage /></LazyPage>} />
        <Route path="/terms" element={<LazyPage><TermsPage /></LazyPage>} />
        <Route path="/privacy" element={<LazyPage><PrivacyPage /></LazyPage>} />
        <Route path="/security" element={<LazyPage><SecurityPage /></LazyPage>} />
        <Route path="/about" element={<LazyPage><AboutPage /></LazyPage>} />
        <Route path="/careers" element={<LazyPage><CareersPage /></LazyPage>} />
        <Route path="/blog" element={<LazyPage><BlogPage /></LazyPage>} />
        <Route path="/use-cases" element={<LazyPage><UseCasesPage /></LazyPage>} />
        <Route path="/consultants" element={<LazyPage><ConsultantsPage /></LazyPage>} />
        <Route path="/partners" element={<LazyPage><PartnersPage /></LazyPage>} />
        <Route path="/hr" element={<LazyPage><HRPage /></LazyPage>} />
        <Route path="/enterprise" element={<LazyPage><EnterprisePage /></LazyPage>} />
        <Route path="/integrations" element={<LazyPage><IntegrationsMarketingPage /></LazyPage>} />
        <Route path="/roi" element={<LazyPage><ROICalculatorPage /></LazyPage>} />
        <Route path="/compare" element={<LazyPage><CompareSurveysPage /></LazyPage>} />
        <Route path="/pilot" element={<LazyPage><PilotCheckoutPage /></LazyPage>} />
        <Route path="/pilot/setup" element={<LazyPage><PilotSetupWizard /></LazyPage>} />
        <Route path="/forgot-password" element={<LazyPage><ForgotPasswordPage /></LazyPage>} />
        <Route path="/reset-password" element={<LazyPage><ResetPasswordPage /></LazyPage>} />
        <Route path="/report/:token" element={<LazyPage><ReportViewPage /></LazyPage>} />
        <Route path="/ref/:slug" element={<LazyPage><ReferralLandingPage /></LazyPage>} />

        {/* Protected route - Debrief (mobile-first, outside Layout) */}
        <Route
          path="/debrief"
          element={
            <ProtectedRoute>
              <LazyPage><DebriefPage /></LazyPage>
            </ProtectedRoute>
          }
        />

        {/* Protected routes - nested under /app */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <PasswordGate>
                <Layout />
              </PasswordGate>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/app/campaigns" replace />} />
          <Route path="campaigns" element={<CampaignsRoute><LazyPage><DashboardPage /></LazyPage></CampaignsRoute>} />
          <Route path="campaigns/:campaignId" element={<CampaignsRoute><LazyPage><CampaignPage /></LazyPage></CampaignsRoute>} />
          <Route path="campaigns/:campaignId/setup" element={<CampaignsRoute><LazyPage><CampaignSetupPage /></LazyPage></CampaignsRoute>} />
          <Route path="campaigns/compare" element={<CampaignsRoute><LazyPage><CampaignComparePage /></LazyPage></CampaignsRoute>} />
          <Route path="campaigns/compare/:sessionId" element={<CampaignsRoute><LazyPage><CampaignComparePage /></LazyPage></CampaignsRoute>} />
          <Route path="debrief" element={<DebriefsRoute><LazyPage><DailyPulsePage /></LazyPage></DebriefsRoute>}>
            <Route index element={<LazyPage><DebriefDashboardPage /></LazyPage>} />
            <Route path="settings" element={<LazyPage><DebriefConfigPage /></LazyPage>} />
            <Route path="settings/new" element={<LazyPage><DebriefConfigPage /></LazyPage>} />
            <Route path="settings/:configId" element={<LazyPage><DebriefConfigPage /></LazyPage>} />
            <Route path="settings/:configId/members" element={<LazyPage><DebriefMembersPage /></LazyPage>} />
          </Route>
          {/* Legacy redirects for old config URLs */}
          <Route path="debrief/config" element={<Navigate to="/app/debrief/settings" replace />} />
          <Route path="debrief/config/*" element={<Navigate to="/app/debrief/settings" replace />} />

          {/* Consolidated Settings */}
          <Route path="settings" element={<LazyPage><SettingsPage /></LazyPage>}>
            <Route path="profile" element={<LazyPage><ProfileSection /></LazyPage>} />
            <Route path="organization" element={<LazyPage><OrganizationSection /></LazyPage>} />
            <Route path="integrations" element={<LazyPage><IntegrationsSection /></LazyPage>} />
            <Route path="billing" element={<LazyPage><BillingSection /></LazyPage>} />
          </Route>

          {/* Legacy routes - redirect to consolidated Settings */}
          <Route path="team" element={<Navigate to="/app/settings/organization" replace />} />
          <Route path="billing" element={<Navigate to="/app/settings/billing" replace />} />
          <Route path="partner" element={<LazyPage><PartnerDashboardPage /></LazyPage>} />
          <Route path="partner/organizations" element={<LazyPage><PartnerOrganizationsPage /></LazyPage>} />
          <Route path="partner/revenue" element={<LazyPage><PartnerRevenuePage /></LazyPage>} />
          {/* Legacy settings routes - redirect to consolidated */}
          <Route path="settings/hris" element={<Navigate to="/app/settings/integrations" replace />} />
          <Route path="settings/api-keys" element={<Navigate to="/app/settings/integrations" replace />} />
          <Route path="onboarding" element={<LazyPage><OnboardingPulsePage /></LazyPage>} />
          <Route path="admin" element={<LazyPage><AdminDashboard /></LazyPage>} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/campaigns" element={<Navigate to="/app/campaigns" replace />} />
        <Route path="/campaigns/*" element={<Navigate to="/app/campaigns" replace />} />
        <Route path="/members" element={<Navigate to="/app/team" replace />} />
        <Route path="/team" element={<Navigate to="/app/team" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App
